import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Container, Row, Col, Button, Spinner, Alert, Badge, Modal, Form, Tooltip, OverlayTrigger } from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import { useNavigate, useLocation } from 'react-router-dom'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDateTime, formatDateOnly } from '../utils/commonUtils'
import { useTableState, createColumnToggleDropdown } from '../utils/tableUtils'
import ResizableTable from '../components/ResizableTable'
import StickyScrollbar from '../components/StickyScrollbar'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import StockItemInfo from '../components/StockItemInfo'
import '../styles/selectControl.css'

const STATUS_COLORS = { OK: 'success', DECREASE: 'danger', ERROR: 'secondary' }
const STATUS_LABELS = { OK: 'OK', DECREASE: 'Decrease', ERROR: 'Error' }

const initialTableState = {
    visibility: {
        image: true, campaignName: true, asin: true, stockItem: true,
        stock: true, avgDailySales: true, daysRemaining: true,
        status: true, lastEvaluated: true, actions: true,
    },
    widths: {
        image: 'auto', campaignName: 300, asin: 150, stockItem: 250,
        stock: 'auto', avgDailySales: 'auto', daysRemaining: 'auto',
        status: 'auto', lastEvaluated: 'auto', actions: 'auto',
    },
}

const columnLabels = {
    image: 'Image',
    campaignName: 'Campaign',
    asin: 'ASIN',
    stockItem: 'Stock Item',
    stock: 'FBA Stock',
    avgDailySales: 'Avg Daily Sales',
    daysRemaining: 'Days Remaining',
    status: 'Status',
    lastEvaluated: 'Last Evaluated',
    actions: 'Actions',
}

function AmazonBiddingManagementScreen() {
    const navigate = useNavigate()
    const location = useLocation()

    // Account comes from navigation state (hub cards) or from ?account= so links survive a refresh
    const accountFromUrl = new URLSearchParams(location.search).get('account') || ''
    const accountId = location.state?.accountId || accountFromUrl
    const [accountName, setAccountName] = useState(location.state?.accountName || '')
    const backTo = location.state?.backTo || '/amazon-ad-automation'
    const backLabel = location.state?.backLabel || 'Back to Ad Automation'
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [enrollments, setEnrollments] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [evaluating, setEvaluating] = useState(false)

    // ?search= pre-filters the table when arriving from an alert's Details button
    const searchFromUrl = new URLSearchParams(location.search).get('search') || ''
    const [searchTerm, setSearchTerm] = useState(searchFromUrl)
    const [searchInputValue, setSearchInputValue] = useState(searchFromUrl)
    const debounceTimeoutRef = useRef(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)

    const [showAddModal, setShowAddModal] = useState(false)
    const [showSettingsModal, setShowSettingsModal] = useState(false)
    const [selectedEnrollment, setSelectedEnrollment] = useState(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [detailData, setDetailData] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)

    const [campaigns, setCampaigns] = useState([])
    const [campaignsLoading, setCampaignsLoading] = useState(false)

    const [selectedRows, setSelectedRows] = useState([])
    const [selectAll, setSelectAll] = useState(false)

    const { columnState, updateColumnVisibility } = useTableState('amazon_bidding_mgmt', initialTableState)
    const columnVisibility = columnState.visibility

    const toggleColumnVisibility = (columnId) => {
        updateColumnVisibility(columnId, !columnVisibility[columnId])
    }

    useEffect(() => {
        if (!accountId) {
            navigate('/amazon-ad-automation', { replace: true })
        }
    }, [accountId, navigate])

    useEffect(() => {
        if (!accountId || accountName) return
        const loadAccountName = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                const match = (data || []).find(a => String(a.id) === String(accountId))
                if (match) setAccountName(match.account_name)
            } catch (err) {
                setError(formatErrorResponse(err))
            }
        }
        loadAccountName()
    }, [accountId, accountName])

    const loadEnrollments = useCallback(async () => {
        if (!accountId) return
        setLoading(true)
        try {
            const data = await amazonAPI.bidding.getEnrollments(accountId)
            setEnrollments(data || [])
            setError('')
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setLoading(false)
        }
    }, [accountId])

    useEffect(() => {
        loadEnrollments()
    }, [loadEnrollments])

    const badgeStats = useMemo(() => {
        let ok = 0, decrease = 0, errors = 0, enrolled = enrollments.length
        for (const e of enrollments) {
            const st = e.latest_evaluation?.status
            if (st === 'OK') ok++
            else if (st === 'DECREASE') decrease++
            else if (st === 'ERROR') errors++
        }
        return { enrolled, ok, decrease, errors }
    }, [enrollments])

    const filteredEnrollments = useMemo(() => {
        let result = enrollments
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            result = result.filter(e =>
                (e.campaign_name && e.campaign_name.toLowerCase().includes(term)) ||
                (e.latest_evaluation?.asin && e.latest_evaluation.asin.toLowerCase().includes(term))
            )
        }
        if (sortColumn && sortDirection) {
            result = [...result].sort((a, b) => {
                let aVal, bVal
                switch (sortColumn) {
                    case 'campaignName':
                        aVal = (a.campaign_name || '').toLowerCase()
                        bVal = (b.campaign_name || '').toLowerCase()
                        break
                    case 'daysRemaining':
                        aVal = a.latest_evaluation?.days_remaining ?? Infinity
                        bVal = b.latest_evaluation?.days_remaining ?? Infinity
                        break
                    case 'stock':
                        aVal = a.latest_evaluation?.fulfillable_quantity ?? 0
                        bVal = b.latest_evaluation?.fulfillable_quantity ?? 0
                        break
                    case 'avgDailySales':
                        aVal = a.latest_evaluation?.avg_daily_sales ?? 0
                        bVal = b.latest_evaluation?.avg_daily_sales ?? 0
                        break
                    default:
                        return 0
                }
                if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
                if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
                return 0
            })
        }
        return result
    }, [enrollments, searchTerm, sortColumn, sortDirection])

    const totalItems = filteredEnrollments.length
    const startIdx = (currentPage - 1) * pageSize
    const displayedEnrollments = filteredEnrollments.slice(startIdx, startIdx + pageSize)

    useEffect(() => { setCurrentPage(1) }, [searchTerm])

    const handleSearchInputChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => setSearchTerm(value), 500)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
    }

    const handleSort = (colId, direction) => {
        setSortColumn(colId)
        setSortDirection(direction)
    }

    const handleEvaluateAll = async () => {
        setEvaluating(true)
        setError('')
        try {
            await amazonAPI.bidding.evaluate(accountId)
            await loadEnrollments()
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setEvaluating(false)
        }
    }

    const handleDeleteEnrollment = async (enrollment) => {
        if (!window.confirm(`Remove "${enrollment.campaign_name}" from automation?`)) return
        try {
            await amazonAPI.bidding.deleteEnrollment(enrollment.id)
            await loadEnrollments()
        } catch (err) {
            setError(formatErrorResponse(err))
        }
    }

    const handleOpenSettings = (enrollment) => {
        setSelectedEnrollment(enrollment)
        setShowSettingsModal(true)
    }

    const handleViewDetail = async (enrollment) => {
        setSelectedEnrollment(enrollment)
        setShowDetailModal(true)
        setDetailLoading(true)
        try {
            const data = await amazonAPI.bidding.getEvaluation(enrollment.id)
            setDetailData(data)
        } catch {
            setDetailData(null)
        } finally {
            setDetailLoading(false)
        }
    }

    const handleRowSelect = (row, isSelected) => {
        if (isSelected) {
            setSelectedRows(prev => [...prev, row.id])
        } else {
            setSelectedRows(prev => prev.filter(id => id !== row.id))
        }
    }

    const handleSelectAll = (isSelected) => {
        setSelectAll(isSelected)
        if (isSelected) {
            setSelectedRows(displayedEnrollments.map(e => e.id))
        } else {
            setSelectedRows([])
        }
    }

    const isRowSelected = (rowId) => selectedRows.includes(rowId)

    const handleEvaluateSelected = async () => {
        setEvaluating(true)
        setError('')
        try {
            await amazonAPI.bidding.evaluate(accountId, selectedRows)
            setSelectedRows([])
            setSelectAll(false)
            await loadEnrollments()
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setEvaluating(false)
        }
    }

    const columns = [
        {
            id: 'selection',
            header: () => (
                <Form.Check
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    label=""
                />
            ),
            cell: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={isRowSelected(row.id)}
                    onChange={(e) => handleRowSelect(row, e.target.checked)}
                    label=""
                />
            ),
        },
        columnVisibility.image && {
            id: 'image',
            header: 'Image',
            cell: (row) => {
                const eval_ = row.latest_evaluation
                if (!eval_) return null
                return null
            },
        },
        columnVisibility.campaignName && {
            id: 'campaignName',
            header: 'Campaign',
            sortable: true,
            cell: (row) => row.campaign_name || '',
        },
        columnVisibility.asin && {
            id: 'asin',
            header: 'ASIN',
            cell: (row) => row.latest_evaluation?.asin || '',
        },
        columnVisibility.stockItem && {
            id: 'stockItem',
            header: 'Stock Item',
            cell: (row) => '',
        },
        columnVisibility.stock && {
            id: 'stock',
            header: 'FBA Stock',
            sortable: true,
            cell: (row) => {
                const qty = row.latest_evaluation?.fulfillable_quantity
                return qty != null ? qty.toLocaleString() : ''
            },
        },
        columnVisibility.avgDailySales && {
            id: 'avgDailySales',
            header: 'Avg Daily Sales',
            sortable: true,
            cell: (row) => {
                const avg = row.latest_evaluation?.avg_daily_sales
                return avg != null ? Number(avg).toFixed(1) : ''
            },
        },
        columnVisibility.daysRemaining && {
            id: 'daysRemaining',
            header: 'Days Remaining',
            sortable: true,
            cell: (row) => {
                const days = row.latest_evaluation?.days_remaining
                if (days == null) return ''
                const val = Number(days).toFixed(0)
                const color = row.latest_evaluation?.should_decrease ? 'text-danger fw-bold' : ''
                return <span className={color}>{val}</span>
            },
        },
        columnVisibility.status && {
            id: 'status',
            header: 'Status',
            cell: (row) => {
                const eval_ = row.latest_evaluation
                if (!eval_) return <Badge bg="secondary">Not evaluated</Badge>
                return (
                    <Badge bg={STATUS_COLORS[eval_.status] || 'secondary'}>
                        {STATUS_LABELS[eval_.status] || eval_.status}
                    </Badge>
                )
            },
        },
        columnVisibility.lastEvaluated && {
            id: 'lastEvaluated',
            header: 'Last Evaluated',
            cell: (row) => {
                const at = row.latest_evaluation?.evaluated_at
                return at ? formatDateTime(at) : ''
            },
        },
        columnVisibility.actions && {
            id: 'actions',
            header: 'Actions',
            cell: (row) => (
                <div className="d-flex gap-1">
                    {row.latest_evaluation && (
                        <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetail(row) }}>
                            Details
                        </Button>
                    )}
                    <Button variant="outline-primary" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenSettings(row) }}>
                        Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteEnrollment(row) }} title="Remove">
                        <i className="fas fa-times"></i>
                    </Button>
                </div>
            ),
        },
    ].filter(Boolean)

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <h1 className="mb-0">Inventory-Aware Bidding</h1>
                    {accountName && (
                        <Badge bg="primary" className="ms-3 fs-6">{accountName}</Badge>
                    )}
                </div>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(backTo, { state: { returnFilters } })}
                >
                    <i className="fas fa-arrow-left me-2"></i>{backLabel}
                </Button>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <Row className="mb-4 g-3">
                <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Enrolled</h5><h3 className="mb-0">{badgeStats.enrolled}</h3></div></Col>
                <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">OK</h5><h3 className="mb-0">{badgeStats.ok}</h3></div></Col>
                <Col><div className="bg-danger text-white p-3 rounded text-center"><h5 className="mb-1">Decrease</h5><h3 className="mb-0">{badgeStats.decrease}</h3></div></Col>
                <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">Errors</h5><h3 className="mb-0">{badgeStats.errors}</h3></div></Col>
            </Row>

            <Row className="mb-3 align-items-end">
                <Col>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchInputChange}
                        onClear={handleClearSearch}
                        placeholder="Search by campaign name or ASIN..."
                        autoComplete="off"
                    />
                </Col>
                <Col xs="auto">
                    <Button variant="success" onClick={() => setShowAddModal(true)}>
                        <i className="fas fa-plus me-2"></i>Add Campaign
                    </Button>
                </Col>
                <Col xs="auto">
                    {selectedRows.length > 0 ? (
                        <Button
                            variant="primary"
                            onClick={handleEvaluateSelected}
                            disabled={evaluating}
                        >
                            {evaluating ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Evaluating...</>
                            ) : (
                                <><i className="fas fa-play me-2"></i>Evaluate Selected ({selectedRows.length})</>
                            )}
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={handleEvaluateAll}
                            disabled={evaluating || enrollments.length === 0}
                        >
                            {evaluating ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Evaluating...</>
                            ) : (
                                <><i className="fas fa-play me-2"></i>Evaluate All</>
                            )}
                        </Button>
                    )}
                </Col>
            </Row>

            <div className="mb-2">
                {createColumnToggleDropdown(
                    columnVisibility,
                    columnLabels,
                    toggleColumnVisibility,
                )}
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : enrollments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="fas fa-chart-line fa-3x mb-3 d-block"></i>
                    <p>No campaigns enrolled yet. Click &ldquo;Add Campaign&rdquo; to get started.</p>
                </div>
            ) : (
                <>
                    <StickyScrollbar>
                        <ResizableTable
                            columns={columns}
                            data={displayedEnrollments}
                            tableId="amazon_bidding_mgmt"
                            initialWidths={columnState.widths}
                            onSort={handleSort}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                        />
                    </StickyScrollbar>
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalItems}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                    />
                </>
            )}

            <AddCampaignModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                accountId={accountId}
                enrolledCampaignIds={enrollments.map(e => e.campaign_id)}
                onAdded={loadEnrollments}
            />

            <SettingsModal
                show={showSettingsModal}
                onHide={() => { setShowSettingsModal(false); setSelectedEnrollment(null) }}
                enrollment={selectedEnrollment}
                onSaved={loadEnrollments}
            />

            <EvaluationDetailModal
                show={showDetailModal}
                onHide={() => { setShowDetailModal(false); setDetailData(null) }}
                enrollment={selectedEnrollment}
                data={detailData}
                loading={detailLoading}
            />
        </Container>
    )
}

function AddCampaignModal({ show, onHide, accountId, enrolledCampaignIds, onAdded }) {
    const campaignsRef = useRef([])
    const [campaignsLoaded, setCampaignsLoaded] = useState(false)
    const [selectedCampaign, setSelectedCampaign] = useState(null)
    const [lookbackDays, setLookbackDays] = useState(28)
    const [thresholdDays, setThresholdDays] = useState(20)
    const [bidDecrease, setBidDecrease] = useState('0.02')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const searchTimerRef = useRef(null)

    useEffect(() => {
        if (!show || !accountId) return
        setCampaignsLoaded(false)
        const load = async () => {
            try {
                const data = await amazonAPI.advertising.getSpCacheData(accountId)
                const allCampaigns = data?.campaigns || []
                campaignsRef.current = allCampaigns
                    .filter(c => c.state === 'ENABLED' && !enrolledCampaignIds.includes(c.campaignId))
                    .map(c => ({ value: c.campaignId, label: c.name }))
                setCampaignsLoaded(true)
            } catch (err) {
                setError(formatErrorResponse(err))
            }
        }
        load()
        setSelectedCampaign(null)
        setLookbackDays(28)
        setThresholdDays(20)
        setBidDecrease('0.02')
        setError('')
    }, [show, accountId, enrolledCampaignIds])

    const loadOptions = useCallback((inputValue, callback) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        if (!inputValue || inputValue.length < 2) {
            callback([])
            return
        }
        searchTimerRef.current = setTimeout(() => {
            const term = inputValue.toLowerCase()
            const results = campaignsRef.current.filter(c =>
                c.label.toLowerCase().includes(term)
            )
            callback(results)
        }, 300)
    }, [])

    const handleSubmit = async () => {
        if (!selectedCampaign) {
            setError('Please select a campaign')
            return
        }
        setSaving(true)
        setError('')
        try {
            await amazonAPI.bidding.createEnrollment({
                seller_account: accountId,
                campaign_id: selectedCampaign.value,
                campaign_name: selectedCampaign.label,
                lookback_days: lookbackDays,
                stock_threshold_days: thresholdDays,
                bid_decrease_amount: bidDecrease,
            })
            onAdded()
            onHide()
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Add Campaign to Bidding Automation</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {!campaignsLoaded ? (
                    <div className="text-center py-3"><Spinner animation="border" /></div>
                ) : (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Campaign</Form.Label>
                            <AsyncSelect
                                value={selectedCampaign}
                                onChange={setSelectedCampaign}
                                loadOptions={loadOptions}
                                defaultOptions={false}
                                noOptionsMessage={({ inputValue }) =>
                                    !inputValue || inputValue.length < 2
                                        ? 'Type at least 2 characters to search...'
                                        : 'No matching campaigns found'
                                }
                                placeholder="Search by campaign name..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                            />
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Lookback Days{' '}
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Number of past days to analyze sales data for calculating average daily sales velocity.</Tooltip>}>
                                            <i className="fas fa-info-circle text-muted"></i>
                                        </OverlayTrigger>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={lookbackDays}
                                        onChange={(e) => setLookbackDays(parseInt(e.target.value) || 28)}
                                        min={7}
                                        max={90}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Stock Threshold (days){' '}
                                        <OverlayTrigger placement="top" overlay={<Tooltip>If estimated days of stock remaining falls below this threshold, a bid decrease will be recommended.</Tooltip>}>
                                            <i className="fas fa-info-circle text-muted"></i>
                                        </OverlayTrigger>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={thresholdDays}
                                        onChange={(e) => setThresholdDays(parseInt(e.target.value) || 20)}
                                        min={1}
                                        max={90}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Bid Decrease ($)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        value={bidDecrease}
                                        onChange={(e) => setBidDecrease(e.target.value)}
                                        min={0.01}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={saving || !selectedCampaign}>
                    {saving ? <Spinner animation="border" size="sm" /> : 'Add Campaign'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

function SettingsModal({ show, onHide, enrollment, onSaved }) {
    const [lookbackDays, setLookbackDays] = useState(28)
    const [thresholdDays, setThresholdDays] = useState(20)
    const [bidDecrease, setBidDecrease] = useState('0.02')
    const [isActive, setIsActive] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (enrollment) {
            setLookbackDays(enrollment.lookback_days || 28)
            setThresholdDays(enrollment.stock_threshold_days || 20)
            setBidDecrease(enrollment.bid_decrease_amount || '0.02')
            setIsActive(enrollment.is_active !== false)
            setError('')
        }
    }, [enrollment])

    const handleSave = async () => {
        setSaving(true)
        setError('')
        try {
            await amazonAPI.bidding.updateEnrollment(enrollment.id, {
                lookback_days: lookbackDays,
                stock_threshold_days: thresholdDays,
                bid_decrease_amount: bidDecrease,
                is_active: isActive,
            })
            onSaved()
            onHide()
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Enrollment Settings</Modal.Title>
            </Modal.Header>
            {enrollment && (
                <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom" style={{ background: 'var(--bs-primary)', color: '#fff' }}>
                    <i className="fab fa-amazon" style={{ opacity: 0.7 }}></i>
                    <span className="fw-semibold">{enrollment.campaign_name}</span>
                </div>
            )}
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <div className="d-flex align-items-start justify-content-between py-3 border-bottom">
                    <div>
                        <Form.Label className="fw-semibold mb-1">Lookback Days</Form.Label>
                        <p className="text-muted mb-0" style={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                            Number of past days to analyze sales data for calculating average daily sales velocity.
                        </p>
                    </div>
                    <Form.Control
                        type="number"
                        value={lookbackDays}
                        onChange={(e) => setLookbackDays(parseInt(e.target.value) || 28)}
                        min={7}
                        max={90}
                        style={{ width: '90px', textAlign: 'center', flexShrink: 0 }}
                        className="ms-3"
                    />
                </div>
                <div className="d-flex align-items-start justify-content-between py-3 border-bottom">
                    <div>
                        <Form.Label className="fw-semibold mb-1">Stock Threshold (days)</Form.Label>
                        <p className="text-muted mb-0" style={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                            If estimated days of stock remaining falls below this threshold, a bid decrease will be recommended.
                        </p>
                    </div>
                    <Form.Control
                        type="number"
                        value={thresholdDays}
                        onChange={(e) => setThresholdDays(parseInt(e.target.value) || 20)}
                        min={1}
                        max={90}
                        style={{ width: '90px', textAlign: 'center', flexShrink: 0 }}
                        className="ms-3"
                    />
                </div>
                <div className="d-flex align-items-start justify-content-between py-3 border-bottom">
                    <div>
                        <Form.Label className="fw-semibold mb-1">Bid Decrease ($)</Form.Label>
                        <p className="text-muted mb-0" style={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                            Dollar amount subtracted from each keyword and target bid when stock is low.
                        </p>
                    </div>
                    <Form.Control
                        type="number"
                        step="0.01"
                        value={bidDecrease}
                        onChange={(e) => setBidDecrease(e.target.value)}
                        min={0.01}
                        style={{ width: '90px', textAlign: 'center', flexShrink: 0 }}
                        className="ms-3"
                    />
                </div>
                <div className="d-flex align-items-center justify-content-between py-3">
                    <Form.Label className="fw-semibold mb-0">Active</Form.Label>
                    <Form.Check
                        type="switch"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

function EvalStatCard({ label, value, sub, danger }) {
    return (
        <div className={`border rounded p-2 text-center flex-fill${danger ? ' border-danger' : ''}`} style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }} className="text-muted mb-1">{label}</div>
            <div className={`fw-bold${danger ? ' text-danger' : ''}`} style={{ fontSize: '1.4rem', lineHeight: 1.1 }}>{value}</div>
            {sub && <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '0.15rem' }}>{sub}</div>}
        </div>
    )
}

function EvaluationDetailModal({ show, onHide, enrollment, data, loading }) {
    const verdictBg = data?.should_decrease
        ? { background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.2)' }
        : { background: 'rgba(25,135,84,0.08)', border: '1px solid rgba(25,135,84,0.2)' }

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Evaluation Details</Modal.Title>
            </Modal.Header>
            {enrollment && (
                <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom" style={{ background: 'var(--bs-primary)', color: '#fff' }}>
                    <i className="fab fa-amazon" style={{ opacity: 0.7 }}></i>
                    <span className="fw-semibold">{enrollment.campaign_name}</span>
                </div>
            )}
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : !data ? (
                    <p className="text-muted">No evaluation data available.</p>
                ) : (
                    <>
                        {data.error_message && (
                            <Alert variant="warning" className="mb-3">{data.error_message}</Alert>
                        )}

                        <div className="d-flex gap-2 mb-3">
                            <EvalStatCard
                                label="FBA Stock"
                                value={data.fulfillable_quantity != null ? data.fulfillable_quantity.toLocaleString() : ''}
                                sub="units"
                            />
                            <EvalStatCard
                                label="Daily Sales"
                                value={data.avg_daily_sales != null ? Number(data.avg_daily_sales).toFixed(1) : ''}
                                sub="units/day"
                            />
                            <EvalStatCard
                                label="Days Left"
                                value={data.days_remaining != null ? Number(data.days_remaining).toFixed(0) : ''}
                                sub={data.should_decrease ? 'below threshold' : 'above threshold'}
                                danger={data.should_decrease}
                            />
                            <EvalStatCard
                                label="Stockout"
                                value={data.projected_stockout_date ? formatDateOnly(data.projected_stockout_date) : ''}
                                sub="projected"
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2 rounded p-2 mb-3" style={verdictBg}>
                            <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{
                                    width: 32, height: 32,
                                    background: data.should_decrease ? '#dc3545' : '#198754',
                                    color: '#fff',
                                }}>
                                <i className={`fas ${data.should_decrease ? 'fa-arrow-trend-down' : 'fa-check'}`}></i>
                            </div>
                            <div>
                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                    {data.should_decrease ? 'Decrease Bids Recommended' : 'No Action Needed'}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                    {data.should_decrease
                                        ? `Stock will last ~${data.days_remaining != null ? Number(data.days_remaining).toFixed(0) : '?'} days at current velocity (threshold: ${data.threshold_days_used} days)`
                                        : `Stock level is healthy (${data.days_remaining != null ? Number(data.days_remaining).toFixed(0) : '?'} days remaining, threshold: ${data.threshold_days_used} days)`
                                    }
                                </div>
                            </div>
                        </div>

                        {data.sales_data && data.sales_data.length > 0 && (
                            <div className="border-top pt-3 mb-3">
                                <h6 className="fw-semibold mb-2">Sales Data</h6>
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>Period</th>
                                            <th>Units Sold</th>
                                            <th>Days</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.sales_data.map((sd, idx) => (
                                            <tr key={idx}>
                                                <td>{formatDateOnly(sd.period_start)} - {formatDateOnly(sd.period_end)}</td>
                                                <td>{sd.units_sold}</td>
                                                <td>{sd.days}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {data.recommendations && data.recommendations.length > 0 && (
                            <div className="border-top pt-3 mb-3">
                                <h6 className="fw-semibold mb-2">Bid Recommendations ({data.recommendations.length})</h6>
                                <table className="table table-sm mb-3">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Keyword / Target</th>
                                            <th>Match Type</th>
                                            <th>Current Bid</th>
                                            <th>Suggested Bid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.recommendations.map((rec, idx) => (
                                            <tr key={idx}>
                                                <td><Badge bg={rec.target_type === 'KEYWORD' ? 'primary' : 'info'}>{rec.target_type}</Badge></td>
                                                <td>{rec.keyword_text || rec.expression_text || ''}</td>
                                                <td>{rec.match_type || ''}</td>
                                                <td>${Number(rec.current_bid).toFixed(2)}</td>
                                                <td className="text-danger fw-bold">${Number(rec.suggested_bid).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="text-end">
                                    <Button variant="warning" disabled>
                                        Apply Recommendations
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <div className="px-3 py-2 border-top text-muted" style={{ fontSize: '0.78rem', background: 'var(--bs-tertiary-bg)' }}>
                <i className="fas fa-cog me-1"></i>
                Lookback: {data?.lookback_days_used} days &middot; Threshold: {data?.threshold_days_used} days &middot; Decrease: ${data ? Number(data.bid_decrease_used).toFixed(2) : ''} &middot; Evaluated: {data ? formatDateTime(data.evaluated_at) : ''}
            </div>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AmazonBiddingManagementScreen
